import { useMemo } from 'react'
import { useConnections, type Connection } from './use-connections'
import { useIntegrationProviders, type Provider } from './use-providers'

// Connection rows whose provider differs from the catalog entry that owns
// them. The Ashby modes are separate providers on the wire but one "Ashby"
// card in the UI (see ASHBY_CONNECTION_PROVIDERS in @shared/utils/connections).
const CONNECTION_PROVIDER_TO_CATALOG_ID: Record<string, string> = {
  'ashby-mcp': 'ashby',
}

/**
 * Virtual entries `getConnectionsList` synthesizes when the user's ORGANIZATION
 * shares the connection (no personal row exists). Server-controlled marker —
 * these render read-only: there is nothing of the user's to reconnect or
 * disconnect.
 */
export function isOrgProvidedConnection(connection: Connection): boolean {
  return Boolean(connection.metadata?.orgProvided)
}

// The catalog card is `ashby`, but its OAuth connection row is `ashby-mcp`. A
// stored personal `ashby` row is only the optional API-key upgrade, while
// `ashby-client` is a retired local-session row — neither establishes the card
// on its own. The org-provided virtual `ashby` entry DOES establish it.
function isCatalogConnection(connection: Connection): boolean {
  if (isOrgProvidedConnection(connection)) return true
  return connection.providerName !== 'ashby' && connection.providerName !== 'ashby-client'
}

function toCatalogId(connection: Connection): string {
  return CONNECTION_PROVIDER_TO_CATALOG_ID[connection.providerName] ?? connection.providerName
}

// An API-key-only Ashby setup (a personal `ashby` row with no `ashby-mcp` OAuth
// row) can no longer enable the toolset now that MCP is the primary connection.
// Surface it as an expired `ashby` card so the user is prompted to reconnect via
// OAuth. Once `ashby-mcp` is connected the row appears and this special case
// turns itself off. When the ORG provides the connection there is nothing to
// reconnect — the org entry supersedes the orphaned personal key entirely.
function hasOrphanedAshbyApiKey(connections: Array<Connection>): boolean {
  const hasApiKey = connections.some(
    (connection) => connection.providerName === 'ashby' && !isOrgProvidedConnection(connection),
  )
  const hasMcp = connections.some((connection) => connection.providerName === 'ashby-mcp')
  const hasOrgAshby = connections.some(
    (connection) => connection.providerName === 'ashby' && isOrgProvidedConnection(connection),
  )
  return hasApiKey && !hasMcp && !hasOrgAshby
}

export function getUsableIntegrationProviderIds(connections: Array<Connection> | undefined): Set<string> {
  return new Set(
    connections?.filter((connection) => !connection.hasExpired && isCatalogConnection(connection)).map(toCatalogId) ??
      [],
  )
}

export function getConnectedIntegrationProviderIds(connections: Array<Connection> | undefined): Set<string> {
  const ids = new Set(connections?.filter(isCatalogConnection).map(toCatalogId) ?? [])
  if (connections && hasOrphanedAshbyApiKey(connections)) ids.add('ashby')
  return ids
}

/** Catalog ids whose connection is provided by the user's organization (read-only cards). */
export function getOrgProvidedIntegrationProviderIds(connections: Array<Connection> | undefined): Set<string> {
  return new Set(connections?.filter(isOrgProvidedConnection).map(toCatalogId) ?? [])
}

/**
 * Catalog ids where the row is the user's OWN but its calls run on the
 * organization's shared credential with the member's delegated identity (Ashby
 * `ashby-mcp` + org key, no personal API key). Server-controlled marker. These
 * stay fully interactive — the personal row is still theirs to disconnect — but
 * they depend on an org mapping, so they get the same self-serve resync as the
 * read-only org-provided cards.
 */
export function getOrgBackedIntegrationProviderIds(connections: Array<Connection> | undefined): Set<string> {
  return new Set(connections?.filter((connection) => Boolean(connection.metadata?.orgBacked)).map(toCatalogId) ?? [])
}

/**
 * Catalog ids whose establishing connection rows have ALL expired and need
 * reconnecting. A provider can hold several rows (e.g. reconnecting with a
 * different account inserts a new one), so a stale row must not outvote a
 * healthy one.
 */
export function getExpiredIntegrationProviderIds(connections: Array<Connection> | undefined): Set<string> {
  const usable = getUsableIntegrationProviderIds(connections)
  const ids = new Set(
    connections
      ?.filter(isCatalogConnection)
      .filter((connection) => connection.hasExpired)
      .map(toCatalogId)
      .filter((id) => !usable.has(id)) ?? [],
  )
  if (connections && hasOrphanedAshbyApiKey(connections)) ids.add('ashby')
  return ids
}

/**
 * The role whose day-to-day *is* the ATS, so Ashby leads the integrations list
 * instead of sitting wherever the catalog places it. Must match the canonical
 * role label served by the onboarding role-options config
 * (`onboarding-role-options.json`, mirrored by `FALLBACK_ROLES` in
 * `useRoleAndIndustrySelection`) — a drifted label just never matches, leaving
 * catalog order.
 */
const ASHBY_FIRST_ROLE = 'HR/Recruiting'

/** Catalog order with Ashby pulled to the front for {@link ASHBY_FIRST_ROLE}. */
export function withAshbyFirst(providers: Array<Provider>, role: string | undefined): Array<Provider> {
  if (role !== ASHBY_FIRST_ROLE) return providers
  const index = providers.findIndex((provider) => provider.id === 'ashby')
  // Absent or already first — return the same array so callers keep their identity.
  if (index <= 0) return providers
  return [providers[index], ...providers.filter((_, i) => i !== index)]
}

/**
 * Returns toolset providers split into active (connected) and available (not
 * connected), plus the subset of active providers whose connection has expired.
 */
export function useIntegrationConnections() {
  const connectionsQuery = useConnections()
  const providersQuery = useIntegrationProviders()

  const { active, available, expired, orgProvided, orgBacked } = useMemo(() => {
    if (!providersQuery.data) {
      return { active: [], available: [], expired: [], orgProvided: new Set<string>(), orgBacked: new Set<string>() }
    }
    const connectedProviders = getConnectedIntegrationProviderIds(connectionsQuery.data)
    const expiredProviders = getExpiredIntegrationProviderIds(connectionsQuery.data)
    const active = providersQuery.data.filter((p) => connectedProviders.has(p.id))
    return {
      active,
      available: providersQuery.data.filter((p) => !connectedProviders.has(p.id)),
      expired: active.filter((p) => expiredProviders.has(p.id)),
      orgProvided: getOrgProvidedIntegrationProviderIds(connectionsQuery.data),
      orgBacked: getOrgBackedIntegrationProviderIds(connectionsQuery.data),
    }
  }, [connectionsQuery.data, providersQuery.data])

  return {
    active,
    available,
    expired,
    /** Catalog ids of connections the user's organization provides (read-only cards). */
    orgProvided,
    /** Catalog ids whose own row runs on the org's credential (interactive, but resyncable). */
    orgBacked,
    isLoading: connectionsQuery.isLoading || providersQuery.isLoading,
  }
}
